import { z } from 'zod';

/**
 * UI control metadata for a preset config field (A5b.6). The control is *data*
 * keyed by `"<presetKey>.<fieldKey>"` so per-preset forms never hardcode field
 * names — the schema still supplies type/required/ranges, this only upgrades a
 * primitive field to a richer widget (media picker, duration, multiline text).
 *
 * Invariant: a control changes *how* a field renders, never what it means. The
 * server's `compilePreset` keeps reading the same scalar config keys, so adding
 * a control here is safe without a contract change.
 */
export type PresetConfigControl =
  | { kind: 'media' }
  | { kind: 'text'; multiline?: boolean }
  | { kind: 'duration'; unit: 's' }
  | { kind: 'fields' }
  | { kind: 'threshold' };

/** Validates every registry entry (the registry is data — keep it honest). */
export const PresetConfigControlSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('media') }),
  z.object({ kind: z.literal('text'), multiline: z.boolean().optional() }),
  z.object({ kind: z.literal('duration'), unit: z.literal('s') }),
  z.object({ kind: z.literal('fields') }),
  z.object({ kind: z.literal('threshold') })
]);

/**
 * Field-control registry. Keys are `"<presetKey>.<fieldKey>"`; a key that names
 * no real preset field is a bug (asserted by tests). Fields absent from this map
 * render as a plain input, which is the existing behaviour.
 */
export const PRESET_CONFIG_CONTROLS: Record<string, PresetConfigControl> = {
  // A5b.6 — signage form.
  'signage.headline': { kind: 'text' },
  'signage.media_id': { kind: 'media' },
  'signage.duration_s': { kind: 'duration', unit: 's' },
  // A5b.7 — check-in form: the editable visitor field list.
  'checkin.fields': { kind: 'fields' },
  // A5b.8 — dashboard: the optional colour threshold on the live tile.
  'dashboard.threshold': { kind: 'threshold' }
};
